import React from 'react';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { FDataGrid } from '../grid';
import { IForecastingBatchRow } from './bieforecasting.models';

type Props = {
  rows: IForecastingBatchRow[];
  loading?: boolean;
  canCreate?: boolean;
  selectedRowId?: GridRowId | null;
  //onSelect: (row: IForecastingBatchRow | null) => void;
  onSelect: (row: any | null) => void;
  onCreate: () => void;
  onOpen: (row: IForecastingBatchRow) => void;
  onDelete: (row: IForecastingBatchRow) => void | Promise<void>;
};

const stateColor = (
  statecode?: string,
): 'default' | 'info' | 'success' | 'warning' | 'error' => {
  switch (String(statecode || '').toUpperCase()) {
    case 'PUBLISHED':
    case 'QUALITY_VALIDATED':
      return 'success';
    case 'GENERATED':
    case 'BASELINE_VALIDATED':
      return 'info';
    case 'DRAFT':
      return 'warning';
    case 'CANCELLED':
    case 'SUPERSEDED':
      return 'error';
    default:
      return 'default';
  }
};

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
 
export default class ForecastingBatchGrid extends React.PureComponent<Props> {
  private columns = (): GridColDef[] => [
    {
      field: 'nam',
      headerName: 'Forecast Batch',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => (
        <Stack spacing={0.25} sx={{ py: 0.5, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {params.row.nam}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.shc}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'fiy',
      headerName: 'Fiscal Year',
      width: 110,
    },
    {
      field: 'hms',
      headerName: 'History Window',
      width: 135,
      renderCell: (params) => `${Number(params.row.hms || 0)} months`,
    },
    {
      field: 'forecastperiod',
      headerName: 'Forecast Period',
      minWidth: 210,
      flex: 0.7,
      renderCell: (params) => {
        const row = params.row as IForecastingBatchRow;
        if (!row.fsd && !row.fed) return '-';
        return `${row.fsd || '-'} — ${row.fed || '-'}`;
      },
    },
    {
      field: 'ssc',
      headerName: 'State',
      width: 165,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.ssn || params.row.ssc || 'Unknown'}
          color={stateColor(params.row.ssc)}
          variant={String(params.row.ssc || '').toUpperCase() === 'DRAFT' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'dcd',
      headerName: 'Created',
      width: 185,
      renderCell: (params) => formatDate(params.row.dcd),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 125,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const row = params.row as IForecastingBatchRow;
        const viewOnly = row.openmode === 'VIEW';
        return (
          <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Tooltip title={viewOnly ? 'View Forecast Batch' : 'Open Forecast Batch'}>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  this.props.onOpen(row);
                }}
              >
                {viewOnly ? <OpenInNewIcon fontSize="small" /> : <EditIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={row.deletable ? 'Delete unused Draft batch' : 'Only an unused Draft batch can be deleted'}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!row.deletable || Boolean(this.props.loading)}
                  onClick={(event) => {
                    event.stopPropagation();
                    void this.props.onDelete(row);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  public render(): React.ReactNode {
    return (
      <FDataGrid
        title="Forecast Batches"
        subtitle="Create a governed Forecast Batch or open an existing batch to continue its forecasting wizard."
        rows={this.props.rows || []}
        columns={this.columns()}
        loading={this.props.loading}
        height={500}
        showQuickFilter

        selectionMode={'single'}
        showSelectionControl
        selectedRowId={this.props.selectedRowId}
        onSelectedRowChange={(row) => this.props.onSelect(row)}

        getRowId={(row:any) => row.rid}
        onRowDoubleClick={(params) => this.props.onOpen(params.row as IForecastingBatchRow)}
        toolbarActions={[
          {
            key: 'create-forecast-batch',
            label: 'Create Forecast Batch',
            icon: <AddIcon />,
            disabled: !this.props.canCreate || Boolean(this.props.loading),
            onClick: this.props.onCreate,
          },
        ]}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        //disableRowSelectionOnClick


        
       

        enableCsvExport
        enablePrintExport={false}
        csvFileName="Planning_Cycles"
        csvUtf8WithBom

        toolbarSx={{ backgroundColor: '#eaf2fb' }}
        //getRowId={(r: any) => r.rid}
        //onRowClick={(p: any) => this.props.onSelect(p.row)}
        //pageSizeOptions={[10, 25, 50]}
        //toolbarActions={toolbarActions}
      />
    );
  }
}
